import { useState, type FormEvent } from 'react';
import { useAuth } from '@/lib/auth';
import loginIllustration from '@/assets/illustration-login.png';

const FEATURES = [
  {
    title: 'Secure access',
    body: 'Merchant-scoped API keys and JWT sessions, never a shared platform login.',
  },
  {
    title: 'Sell, recharge, reconcile',
    body: 'Airtime, bundles, ZESA and Telone — one console for vendors and companies alike.',
  },
  {
    title: 'Live float and limits',
    body: 'Watch wallet balances, daily limits and order exceptions as they happen.',
  },
];

export function LoginPage() {
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(email, password);
    } catch (e) {
      // The server returns one message for every wrong combination on purpose; repeating it here
      // keeps the console from telling an attacker which half they got right.
      setError(e instanceof Error ? e.message : 'Sign-in failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-[2fr_3fr]">
      <div className="hidden flex-col overflow-y-auto bg-gradient-to-br from-blue-50 to-ink-100 p-8 lg:flex">
        <div className="text-xl font-bold tracking-tight text-ink-900">
          Recharge<span className="text-blue-600">.</span>
        </div>
        <h1 className="mt-6 text-2xl font-bold leading-tight text-ink-900">
          Buy airtime, bundles and utility tokens — fast and reliably.
        </h1>

        <div className="mt-6 flex flex-col items-center">
          <img src={loginIllustration} alt="" className="w-full max-w-xs" />
          <p className="-mt-2 text-sm font-medium text-ink-600">Secure authentication</p>
        </div>

        <ul className="mt-6 space-y-5">
          {FEATURES.map((f) => (
            <li key={f.title} className="flex gap-3">
              <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-blue-600" />
              <div>
                <div className="font-semibold text-ink-900">{f.title}</div>
                <div className="text-sm text-ink-600">{f.body}</div>
              </div>
            </li>
          ))}
        </ul>

        <div className="mt-8 text-xs font-semibold uppercase tracking-widest text-ink-400">Recharge</div>
      </div>

      <div className="flex items-center justify-center bg-white p-6">
        <form onSubmit={onSubmit} className="w-full max-w-sm">
          <h2 className="text-2xl font-bold text-ink-900">Sign in</h2>
          <p className="mt-1 text-sm text-ink-500">Use the email and password your admin set up for you.</p>

          <label className="mt-6 block">
            <span className="text-sm font-semibold text-ink-800">Email</span>
            <input
              className="input mt-1.5 h-11 text-base focus:border-blue-500 focus:ring-blue-500"
              type="email"
              autoComplete="username"
              placeholder="e.g. dev@company.co.zw"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
            />
          </label>

          <label className="mt-4 block">
            <span className="text-sm font-semibold text-ink-800">Password</span>
            <div className="relative mt-1.5">
              <input
                className="input h-11 pr-16 text-base focus:border-blue-500 focus:ring-blue-500"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute inset-y-0 right-0 px-3 text-sm font-semibold text-ink-500 hover:text-ink-800"
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </label>

          {error && (
            <p className="mt-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={busy}
            className="mt-6 h-11 w-full rounded-lg bg-blue-600 text-sm font-semibold text-white
                       transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {busy ? 'Signing in…' : 'Sign in'}
          </button>

          <p className="mt-5 text-center text-sm text-ink-500">
            Lost access to your account? Contact your admin to reset it.
          </p>
        </form>
      </div>
    </div>
  );
}
